import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import styles from './MultiMapView.module.css';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import Icon from './Icon';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

export interface MapProject {
  id: string;
  name: string;
  location?: string | null;
  latitude: number;
  longitude: number;
}

// Auto-fit the map bounds to show all pins
const FitBounds = ({ projects }: { projects: MapProject[] }) => {
  const map = useMap();
  useEffect(() => {
    if (projects.length === 0) return;
    if (projects.length === 1) {
      map.setView([projects[0].latitude, projects[0].longitude], 12);
      return;
    }
    const bounds = L.latLngBounds(projects.map((p) => [p.latitude, p.longitude]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [projects, map]);
  return null;
};

interface Props {
  projects: MapProject[];
}

const MultiMapView = ({ projects }: Props) => {
  const navigate = useNavigate();

  if (projects.length === 0) {
    return (
      <div className={styles.empty}>
        <Icon name="map" style={{ fontSize: 32, color: '#d1d5db' }} />
        <p>No project locations set yet</p>
      </div>
    );
  }

  const center: [number, number] = [projects[0].latitude, projects[0].longitude];

  return (
    <MapContainer
      center={center}
      zoom={6}
      className={styles.map}
      scrollWheelZoom={false}
      zoomControl
      dragging
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds projects={projects} />
      {projects.map((p) => (
        <Marker key={p.id} position={[p.latitude, p.longitude]}>
          <Popup>
            <div className={styles.popup}>
              <strong>{p.name}</strong>
              {p.location && <span>{p.location}</span>}
              <button onClick={() => navigate(`/projects/${p.id}`)}>
                Open project →
              </button>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default MultiMapView;
